import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { Observable } from 'rxjs';

/**
 * This accept-deny service file is adapted and copied from Legacy code.
 */

@Injectable({
  providedIn: 'root',
})
export class AcceptDenyService {
  private readonly _http = inject(HttpClient);

  private readonly _apiUrl: string;

  constructor() {
    this._apiUrl = environment.apiUrl;
  }

  // -------------------- General Methods --------------------
  denyReason(endPoints: any, userId: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/${endPoints}${userId}`, {});
  }

  handleNewsApproval(deleteId: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/get-reset-updatednews/${deleteId}`, {});
  }

  handleNewsDisapproval(deleteId: number, userId: number): Observable<any> {
    return this._http.delete<any>(`${this._apiUrl}/news/${deleteId}/user/${userId}`, {});
  }

  SectionDataDenyReason(endPoints: any, deleteId: any): Observable<any> {
    const url = `${this._apiUrl}/${endPoints}${deleteId}`;
    return this._http.get<any>(url, { headers: { 'Cache-Control': 'no-cache' } });
  }

  /**
   * These below notification action functions and names are adapted and copied from Legacy code.
   */

  // ----------------------------------------------------------------------------------
  // ------------------------------------ GROUPS --------------------------------------
  // ----------------------------------------------------------------------------------

  joinGroup(groupId: number, userId: number, postData: any): Observable<any> {
    return this._http.put<any>(`${this._apiUrl}/acceptGroup/user/${userId}/group_id/${groupId}`, postData);
  }

  deny(groupId: number, userId: number): Observable<any> {
    return this._http.delete<any>(`${this._apiUrl}/denyGroup/user/${userId}/group_id/${groupId}`, {});
  }

  acceptUpdatedGroup(groupId: number, userId: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/approve-updatedGroupByAdmin/group_id/${groupId}/${userId}`, {});
  }

  unapproveGroup(groupId: number, postData: any): Observable<any> {
    return this._http.put<any>(`${this._apiUrl}/adminDenyGroup/group_id/${groupId}`, postData);
  }

  viewGroup(groupId: number, userId: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/read-notification-updatedgroupByUser/${groupId}/user/${userId}`, {});
  }

  approvedGroup(groupId: number, userId: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/approve-group-by-id/${groupId}/${userId}`, {});
  }

  viewAcceptedGroup(groupId: number, userId: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/getreadGroupInvitaion/${groupId}/user/${userId}`);
  }

  readPublishedGroup(groupId: number, userId: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/read-approvedgroup-notification/${groupId}/user/${userId}`, {});
  }

  deleteGroup(groupId: number): Observable<any> {
    return this._http.delete<any>(`${this._apiUrl}/deleteGroup/${groupId}`, {});
  }

  // ----------------------------------------------------------------------------------
  // ------------------------------------ NEWS ----------------------------------------
  // ----------------------------------------------------------------------------------

  acceptNews(newsId: number, userId: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/approve-news-by-id/${newsId}/${userId}`, {});
  }

  acceptUpdateNews(newsId: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/approve-updatednews/${newsId}/`, {});
  }

  denyNews(newsId: number, postData: any): Observable<any> {
    return this._http.put<any>(`${this._apiUrl}/deny-news/news_id/${newsId}`, postData);
  }

  viewApprovePublishNewsByAdmin(newsId: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/read-approvedpublishednews/news_id/${newsId}`, {});
  }

  viewApproveUpdateNewsByAdmin(newsId: number, userId: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/read-notification-updatednews/${newsId}/user/${userId}`, {});
  }

  // ----------------------------------------------------------------------------------
  // ------------------------------------ EVENTS --------------------------------------
  // ----------------------------------------------------------------------------------

  approvedEvents(eventId: number, userId: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/set-approve-status/${eventId}/approvedby/${userId}`, {});
  }

  approvedUpdateEvents(eventId: number, userId: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/approve-updatedevent/${eventId}/approvedby/${userId}`, {});
  }

  unapprovedEvent(eventId: number, postData: any): Observable<any> {
    return this._http.put<any>(`${this._apiUrl}/deny-event/event_id/${eventId}`, postData);
  }

  denyEvent(eventId: number, postData: any): Observable<any> {
    return this._http.put<any>(`${this._apiUrl}/deny-event/event_id/${eventId}`, postData);
  }

  deleteEvent(eventId: number): Observable<any> {
    return this._http.delete<any>(`${this._apiUrl}/event/${eventId}`, {});
  }

  deleteUpdatedEvent(eventId: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/get-reset-updatedevent/${eventId}`, {});
  }

  acceptInvitedEvent(eventId: number, userId: number, selectedDates: any): Observable<any> {
    return this._http.put<any>(`${this._apiUrl}/acceptEvent/user/${userId}/event_id/${eventId}`, selectedDates);
  }

  denyInvitedEvent(eventId: number, userId: number): Observable<any> {
    return this._http.delete<any>(`${this._apiUrl}/denyEvent/user/${userId}/event_id/${eventId}`, {});
  }

  viewApprovePublishEventByAdmin(eventId: number, userId: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/read-approvedevent-notification/${eventId}/author/${userId}`, {});
  }

  viewApproveUpdateEventByAdmin(eventId: number, userId: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/read-notification-updatedevent/${eventId}/user/${userId}`, {});
  }

  viewUpdateEvent(eventId: number, userId: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/read-notification-updatedeventByUser/${eventId}/user/${userId}`, {});
  }

  // ----------------------------------------------------------------------------------
  // ------------------------------------ TASKS ---------------------------------------
  // ----------------------------------------------------------------------------------

  adminApprovedTasks(taskId: number, userId: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/approve-task-as-admin/${taskId}/approvedby/${userId}`, {});
  }

  adminUnapprovedTasks(taskId: number, postData: any): Observable<any> {
    return this._http.put<any>(`${this._apiUrl}/deny-task/task_id/${taskId}`, postData);
  }

  adminApprovedUpdateTasks(taskId: number, userId: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/approve-updatedtask/${taskId}/approvedby/${userId}`, {});
  }

  deleteTask(taskId: number): Observable<any> {
    return this._http.delete<any>(`${this._apiUrl}/DeleteTask/${taskId}`);
  }

  deleteUpdateTask(taskId: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/get-reset-updatedtask/${taskId}`);
  }

  acceptInvitedTask(taskId: number, userId: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/readtasknotification/${taskId}/user/${userId}`);
  }

  acceptInvitedSubTask(subtaskId: number, userId: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/readsubtasknotification/${subtaskId}/user/${userId}`, {});
  }

  viewUpdatedTask(taskId: number, userId: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/read-notification-updatedtaskByUser/${taskId}/user/${userId}`, {});
  }

  viewCompletedTask(taskId: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/readCompletedTasksNotification/${taskId}`);
  }

  viewAcceptPublishTaskByAdmin(taskId: number, userId: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/read-approvedtask-notification/${taskId}/organizer/${userId}`);
  }

  viewAcceptUpdatedTaskByAdmin(taskId: number, userId: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/read-notification-updatedtask/${taskId}/user/${userId}`);
  }

  // ----------------------------------------------------------------------------------
  // ---------------------------------- MESSAGES --------------------------------------
  // ----------------------------------------------------------------------------------

  acceptMessage(esdb_id: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/message/approve-message/${esdb_id}`, {});
  }

  denyMessage(esdb_id: number): Observable<any> {
    return this._http.delete<any>(`${this._apiUrl}/message/deny-message/${esdb_id}`, {});
  }

  // ----------------------------------------------------------------------------------
  // ------------------------------------ ROOMS ---------------------------------------
  // ----------------------------------------------------------------------------------

  approvedRooms(roomId: number, userId: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/set-approve-room-status/${roomId}/${userId}`, {});
  }

  approvedUpdateRooms(roomId: number, userId: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/approve-updatedrooms/${roomId}/${userId}`, {});
  }

  unapprovedRooms(roomId: number, postData: any): Observable<any> {
    return this._http.put<any>(`${this._apiUrl}/deny-rooms/room_id/${roomId}`, postData);
  }

  viewApprovePublishRoomByAdmin(roomId: number, userId: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/read-approvedroom-notification/${roomId}/author/${userId}`, {});
  }

  viewApproveUpdateRoomByAdmin(roomId: number, userId: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/read-notification-updatedroom/${roomId}/user/${userId}`, {});
  }

  // ----------------------------------------------------------------------------------
  // --------------------------------- INSTRUCTORS ------------------------------------
  // ----------------------------------------------------------------------------------

  approvedInstructors(instructor_id: number, userId: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/set-approve-instructor-status/${instructor_id}/approvedby/${userId}`, {});
  }

  approvedUpdateInstructors(instructor_id: number, userId: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/approve-updatedinstructor/${instructor_id}/approvedby/${userId}`, {});
  }

  unapprovedInstuctors(instructor_id: number, postData: any): Observable<any> {
    return this._http.put<any>(`${this._apiUrl}/deny-instructor/instructor_id/${instructor_id}`, postData);
  }

  viewApproveUpdateInstructorByAdmin(instructorId: number, userId: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/read-notification-updatedinstructor/${instructorId}/user/${userId}`, {});
  }

  viewApprovePublishInstructorByAdmin(instructorId: number, userId: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/read-approvedinstructor-notification/${instructorId}/author/${userId}`, {});
  }

  // ----------------------------------------------------------------------------------
  // ------------------------------------- FAQS ---------------------------------------
  // ----------------------------------------------------------------------------------

  approvedFaqs(faqId: number, userId: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/admin-approve-faq-by-id/${faqId}/approvedby/${userId}`, {});
  }

  approveUpdateFaqs(faqId: number, userId: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/approve-updatedfaq/${faqId}/approvedby/${userId}`, {});
  }

  denyFaqs(faqsId: number, postData: any) {
    return this._http.put<any>(`${this._apiUrl}/deny-faq/faq_id/${faqsId}`, postData);
  }

  readApprovedFaqs(faqId: number, userId: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/read-approvedfaq-notification/${faqId}/author/${userId}`, {});
  }

  readUpdatedFaqs(faqId: number, userId: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/read-notification-updatedfaq/${faqId}/user/${userId}`, {});
  }

  approvedFaqsCategory(faqId: number, userId: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/admin-approve-faqCategory-by-id/${faqId}/${userId}`, {});
  }

  unapprovedFaqsCategory(faqCategoryId: number): Observable<any> {
    return this._http.delete<any>(`${this._apiUrl}/category/${faqCategoryId}`, {});
  }

  // ----------------------------------------------------------------------------------
  // ----------------------------------- SURVEYS --------------------------------------
  // ----------------------------------------------------------------------------------

  approvedSurvey(surveyId: number, userId: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/admin-approve-survey-by-id/${surveyId}/${userId}`, {});
  }

  unapprovedSurvey(surveyId: number, postData: any): Observable<any> {
    return this._http.put<any>(`${this._apiUrl}/deny-survey/survey_id/${surveyId}`, postData);
  }

  approvedUpdatedSurvey(surveyId: number, userId: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/approve-updatedsurvey/survey_id/${surveyId}/${userId}`, {});
  }

  acceptInvitedSurvey(surveyId: number, userId: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/getreadSurveyInvitation/${surveyId}/user/${userId}`, {});
  }

  viewAcceptPublishSurveyByAdmin(surveyId: number, userId: number) {
    return this._http.get<any>(`${this._apiUrl}/read-approvedsurvey-notification/${surveyId}/author/${userId}`);
  }

  viewPublishUpdateSurveyByAdmin(survey_id: number, userId: number) {
    return this._http.get<any>(`${this._apiUrl}/read-notification-updatedsurvey/${survey_id}/user/${userId}`);
  }

  // ----------------------------------------------------------------------------------
  // ------------------------------------ COURSES -------------------------------------
  // ----------------------------------------------------------------------------------

  acceptInvitedCourse(courseId: number, userId: number): Observable<any> {
    return this._http.put<any>(`${this._apiUrl}/acceptCourse/user/${userId}/course_id/${courseId}`, {});
  }

  denyInvitedCourse(courseId: number, userId: number): Observable<any> {
    return this._http.delete<any>(`${this._apiUrl}/denyCourse/user/${userId}/course_id/${courseId}`, {});
  }

  acceptJoinUser(courseId: number, userId: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/adminapprove-waitingtojoin-courserequests/course_id/${courseId}/user_id/${userId}`, {});
  }

  denyJoinUser(courseId: number, userId: number): Observable<any> {
    return this._http.delete<any>(`${this._apiUrl}/admindeny-waitingtojoin-courserequests/course_id/${courseId}/user_id/${userId}`, {});
  }

  approvedCourses(courseId: number, userId: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/set-approve-course-status/${courseId}/${userId}`, {});
  }

  approvedUpdateCourses(courseId: number, userId: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/approve-updatedcourse/${courseId}/${userId}`, {});
  }

  unapprovedCourse(courseId: number, postData: any): Observable<any> {
    return this._http.put<any>(`${this._apiUrl}/deny-course/course_id/${courseId}`, postData);
  }

  deleteUpdateCourse(courseId: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/get-reset-updatedCourses/${courseId}`, {});
  }

  deleteCourse(courseId: number): Observable<any> {
    return this._http.delete<any>(`${this._apiUrl}/deleteCourse/${courseId}`, {});
  }

  viewJoinedCourse(courseId: number, userId: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/read-course-join-message/${courseId}/user/${userId}`, {});
  }

  viewUpdateCourse(courseId: number, userId: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/read-notification-updatedCourseByUser/${courseId}/user/${userId}`, {});
  }

  viewInternalCourse(courseId: number, userId: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/read-courseinternalinstructor-notification/internal-instructor/${userId}/course_id/${courseId}`, {});
  }

  viewInternalUpdateCourse(courseId: number, userId: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/readupdate-courseinternalinstructor-notification/internal-instructor/${userId}/course_id/${courseId}`, {});
  }

  viewApprovePublishCourseByAdmin(courseId: number, userId: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/read-approvedcourse-notification/${courseId}/author/${userId}`, {});
  }

  viewApproveUpdateCourseByAdmin(courseId: number, userId: number): Observable<any> {
    return this._http.get<any>(`${this._apiUrl}/read-notification-updatedcourses/${courseId}/author/${userId}`, {});
  }
}
